import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import Button from '../UI/Button';
import Input from '../UI/Input';
import Toast from '../UI/Toast';
import { PhoneInputWithValidation, PhoneValidationResponse } from '../UI/PhoneInputWithValidation';
import { usherAPI, CreateUsherData } from '../../services/usherAPI';
import { useAppSelector } from '../../store/hooks';
import { logError } from '../../utils/errorLogger';
import { delay } from '../../utils/delay';

interface AddUsherFormProps {
  eventId: string;
  isOpen: boolean;
  onClose: () => void;
  onUsherAdded: (responseData?: any) => void;
}

export const AddUsherForm: React.FC<AddUsherFormProps> = ({
  eventId,
  isOpen,
  onClose,
  onUsherAdded
}) => {
  const { theme } = useAppSelector((state) => state.ui);
  const modalRef = useRef<HTMLDivElement>(null);
  
  const [formData, setFormData] = useState<CreateUsherData>({
    usher_first_name: '',
    usher_last_name: '',
    usher_phone: '',
    usher_email: '',
  });
  
  // Phone validation state
  const [phoneValidation, setPhoneValidation] = useState<PhoneValidationResponse | null>(null);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setFormData({
        usher_first_name: '',
        usher_last_name: '',
        usher_phone: '',
        usher_email: '',
      });
      setPhoneValidation(null);
      setError(null);
      setSuccess(null);
    }
  }, [isOpen]);

  const validateForm = (): string | null => {
    if (!formData.usher_first_name.trim()) {
      return 'First name is required';
    }
    if (!formData.usher_last_name.trim()) {
      return 'Last name is required';
    }
    if (!formData.usher_phone?.trim() && !formData.usher_email?.trim()) {
      return 'Either phone number or email is required';
    }
    if (formData.usher_phone?.trim()) {
      if (!phoneValidation || !phoneValidation.valid) {
        return phoneValidation?.error || 'Please enter a valid phone number';
      }
    }
    if (formData.usher_email?.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.usher_email)) {
        return 'Please enter a valid email address';
      }
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Clean up form data - remove empty strings and unnecessary fields
      const cleanFormData: CreateUsherData = {
        usher_first_name: formData.usher_first_name.trim(),
        usher_last_name: formData.usher_last_name.trim(),
      };

      if (formData.usher_phone?.trim()) {
        if (phoneValidation && phoneValidation.valid) {
          cleanFormData.usher_phone = phoneValidation.normalized;
          // Send country code for proper backend validation
          (cleanFormData as any).country_code = phoneValidation.country;
        } else {
          setError('Please enter a valid phone number');
          setLoading(false);
          return;
        }
      }
      if (formData.usher_email?.trim()) {
        cleanFormData.usher_email = formData.usher_email.trim();
      }

      const response = await usherAPI.createUsher(eventId, cleanFormData);
      
      setSuccess('Usher added successfully!');
      
      // Reset form
      setFormData({
        usher_first_name: '',
        usher_last_name: '',
        usher_phone: '',
        usher_email: '',
      });
      setPhoneValidation(null);
      
      // Call the callback to refresh the usher list immediately
      // Pass the response data if available
      onUsherAdded(response);
      
      // Close the modal after a short delay to show success message
      // Note: handleUsherAdded in UsherManagement will also close it, so this is a fallback
      await delay(1000);
      onClose();
      
    } catch (error: any) {
      logError('Error creating usher', error);
      const errorMessage = error.response?.data?.error?.message || 'Failed to create usher. Please try again.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div 
        ref={modalRef}
        className={`rounded-lg p-6 w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto shadow-2xl ${
          theme === 'dark' ? 'bg-gray-800' : 'bg-white'
        }`}
        style={{
          position: 'relative',
          transform: 'translateY(0)',
          margin: 'auto'
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className={`text-xl font-semibold ${
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>
            Add Usher
          </h2>
          <button
            onClick={handleClose}
            disabled={loading}
            className={`p-2 rounded-lg transition-colors ${
              theme === 'dark'
                ? 'hover:bg-gray-700 text-gray-400 hover:text-white'
                : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'
            } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* First Name */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
            }`}>
              First Name *
            </label>
            <Input
              type="text"
              value={formData.usher_first_name}
              onChange={(e) => setFormData(prev => ({ ...prev, usher_first_name: e.target.value }))}
              placeholder="Enter first name"
              disabled={loading}
              className="w-full"
              required
            />
          </div>

          {/* Last Name */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
            }`}>
              Last Name *
            </label>
            <Input
              type="text"
              value={formData.usher_last_name}
              onChange={(e) => setFormData(prev => ({ ...prev, usher_last_name: e.target.value }))}
              placeholder="Enter last name"
              disabled={loading}
              className="w-full"
              required
            />
          </div>

          {/* Phone Number */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
            }`}>
              Phone Number
            </label>
            <PhoneInputWithValidation
              value={formData.usher_phone || ''}
              onChange={(phoneValue, countryCodeValue) => {
                setFormData(prev => ({ ...prev, usher_phone: phoneValue }));
              }}
              onValidationChange={setPhoneValidation}
              showValidationResult={false}
              placeholder="Enter phone number"
              disabled={loading}
              dropdownZIndex={10001}
            />
          </div>

          {/* Email */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
            }`}>
              Email Address
            </label>
            <Input
              type="email"
              value={formData.usher_email}
              onChange={(e) => setFormData(prev => ({ ...prev, usher_email: e.target.value }))}
              placeholder="Enter email address"
              disabled={loading}
              className="w-full"
            />
          </div>

          {/* Info Text */}
          <div className={`p-3 rounded-lg ${
            theme === 'dark' 
              ? 'bg-blue-900/20 border border-blue-700/30' 
              : 'bg-blue-50 border border-blue-200'
          }`}>
            <p className={`text-sm ${
              theme === 'dark' ? 'text-blue-300' : 'text-blue-700'
            }`}>
              <strong>Note:</strong> Phone number is required.
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <Toast
              type="error"
              message={error}
              onClose={() => setError(null)}
            />
          )}

          {/* Success Message */}
          {success && (
            <Toast
              type="success"
              message={success}
              onClose={() => setSuccess(null)}
            />
          )}

          {/* Action Buttons */}
          <div className="flex items-center justify-end space-x-3 pt-4">
            <Button
              type="button"
              variant="secondary"
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="flex items-center space-x-2"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Adding...</span>
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  <span>Add Usher</span>
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default AddUsherForm;
